import React, { useState, useEffect, useCallback } from 'react';
import { injectIntl } from 'react-intl';
import { useQueryClient, useQuery } from 'react-query';
import { findIndex } from 'lodash';

import { ImagePlaceholder, QuantityButton, Button, Input } from 'components';
import { WarningIcon } from 'icons';

import Recipient from '../recipient';

import defaultBag from 'constants';
import messages from 'messages';

function ShoppingAmount({ item, ...props }) {
    const queryClient = useQueryClient();
    const restaurant = queryClient.getQueryData('restaurant');
    const { formatMessage } = props.intl;

    const bag = useQuery('bag')?.data || defaultBag;

    const [action, setAction] = useState('add');
    const [index, setIndex] = useState(null);
    const [color, setColor] = useState(0);
    const [quantity, setQuantity] = useState(0);
    const [amount, setAmount] = useState(0);
    const [recipient, setRecipient] = useState([]);
    const [update, setUpdate] = useState(false);
    const [added, setAdded] = useState(false);
    const [focus, setFocus] = useState(false);
    const [sameReceiver, setSameReceiver] = useState(false);
    const [sameMessage, setSameMessage] = useState(false);
    const [hash] = useState(
        Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
    );
    const currency = restaurant.currency.toUpperCase();
    const minAmount = restaurant.apps.giftCard?.minAmount
        ? parseFloat(restaurant.apps.giftCard.minAmount)
        : 0;
    const maxAmount = restaurant.apps.giftCard?.maxAmount
        ? parseFloat(restaurant.apps.giftCard.maxAmount)
        : null;
    const [errorMinAmount, setErrorMinAmount] = useState(false);
    const [errorMaxAmount, setErrorMaxAmount] = useState(false);

    const handleCountIncrease = () => {
        setQuantity(quantity + 1);
        setUpdate(true);
        setAdded(false);

        const list = recipient;
        list.push({
            index: recipient.length,
            name: null,
            email: null,
            message: null,
            visible: true,
            edit: true,
            error: false,
        });
        setRecipient(list);
    };

    const handleCountDecrease = () => {
        const list = recipient;
        list.pop();
        setRecipient(list);
        setAdded(false);

        setQuantity(quantity - 1);
    };

    const handleSetQuantity = (value) => {
        const list = recipient;
        for (let i = list.length; i < value; i++) {
            list.push({
                index: recipient.length,
                name: null,
                email: null,
                message: null,
                visible: true,
                edit: true,
                error: false,
            });
        }
        setRecipient(list);
        setQuantity(parseInt(value));
    };

    const addToBag = () => {
        setAdded(true);

        if (verifyData()) {
            const data = {
                color,
                amount,
                quantity,
                sameReceiver,
                index: hash,
                type: 'amount',
                name: 'amount',
                recipients: recipient,
                date: new Date(),
            };

            const bagData = bag;
            const item = findIndex(bagData, { index: index ? index : hash });

            if (item !== -1) {
                bagData[item] = data;
            } else {
                bagData.push(data);
            }

            queryClient.setQueryData('bag', bagData);
            localStorage.setItem('bag', JSON.stringify(bagData));

            if (item !== -1) {
                showNotify('info', formatMessage(messages.genericUpdatedToBag));
            } else {
                showNotify('success', formatMessage(messages.genericAddedToBag));
            }
            toggleBag();

            setAction('update');
        }
    };

    const verifyData = () => {
        if (quantity && amount && checkRecipient()) {
            if (minAmount) {
                if (amount < minAmount) {
                    return false;
                } else {
                    return true;
                }
            }

            return true;
        }

        showNotify('error', formatMessage(messages.genericInformationMissing));
        return false;
    };

    const showNotify = (type, text) => {
        queryClient.setQueryData('notify', {
            type,
            text,
            action: 'show',
        });

        hideNotify();
    };

    const hideNotify = () => {
        setTimeout(() => {
            queryClient.setQueryData('notify', (prevData) => ({
                ...prevData,
                action: '',
            }));
        }, 5000);
    };

    const toggleBag = () => {
        queryClient.setQueryData('app', (prevData) => ({
            ...prevData,
            toggleBag: true,
        }));

        setTimeout(() => {
            queryClient.setQueryData('app', (prevData) => ({
                ...prevData,
                toggleBag: false,
            }));
        }, 5000);
    };

    const checkRecipient = useCallback(() => {
        if (recipient.length) {
            let check = false;

            recipient.forEach((item) => {
                check = false;

                if (item.name !== null && item.email !== null) {
                    check = true;
                }
            });

            return check;
        }

        return false;
    });

    const addRecipient = useCallback((data) => {
        if (data) {
            const list = recipient;

            if (data.name || data.email || data.message) {
                list[data.index] = data;
            } else {
                list[data.index] = {
                    name: null,
                    email: null,
                    message: null,
                    index: data.index,
                    visible: true,
                    edit: true,
                    error: false,
                };
            }

            setRecipient(list);

            if ('sameReceiver' in data) {
                setSameReceiver(data.sameReceiver);
            }

            if ('sameMessage' in data) {
                setSameMessage(data.sameMessage);
            }
        }
    }, []);

    const resolveRecipient = () => {
        if (recipient.length) {
            const list = recipient.map((item, index) => {
                if (index !== 0) {
                    item = {
                        ...item,
                        name: sameReceiver ? recipient[0].name : item.name,
                        email: sameReceiver ? recipient[0].email : item.email,
                        message: sameMessage || sameReceiver ? recipient[0].message : item.message,
                        edit: !sameMessage,
                        visible: !sameReceiver,
                    };
                }

                return item;
            });

            setRecipient(list);
        }
    };

    useEffect(() => {
        setUpdate(false);
    }, [update]);

    useEffect(() => {
        resolveRecipient();
    }, [sameReceiver, sameMessage, JSON.stringify(recipient), quantity, amount]);

    useEffect(() => {
        if (minAmount) {
            if (amount) {
                if (amount < minAmount) {
                    setErrorMinAmount(true);
                } else {
                    setErrorMinAmount(false);
                }
            }
        }

        if (maxAmount) {
            if (amount) {
                if (amount > maxAmount) {
                    setErrorMaxAmount(true);
                } else {
                    setErrorMaxAmount(false);
                }
            }
        }
    }, [quantity, amount, JSON.stringify(recipient)]);

    useEffect(() => {
        setColor(item);

        if (item.date) {
            setAmount(item.amount);
            setQuantity(item.quantity);
            setColor(item.color);
            setRecipient(item.recipients || []);
            setIndex(item.index);
            setAction('update');

            if (item.sameReceiver) {
                setSameReceiver(item.sameReceiver);
            }

            if (item.sameMessage) {
                setSameMessage(item.sameMessage);
            }
        }
    }, [item]);

    return (
        <div className="shopping">
            <div className="wrapper">
                <ImagePlaceholder color={color} text={formatMessage(messages.genericLabelAmount)} />

                <Input
                    id="amount"
                    type="number"
                    min={minAmount}
                    step="0.10"
                    placeholder={`${formatMessage(messages.genericLabelAmount)} (${currency})`}
                    value={amount}
                    onChange={(v) => setAmount(v)}
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                    customStyles={{
                        margin: '10px auto 8px',
                        width: '100%',
                    }}
                    data-test="amount-value"
                />
                {minAmount && focus ? (
                    <div data-test="amount-foot-note" className="foot-note">
                        <p>
                            {formatMessage(messages.genericLabelAmount)} ({currency}){' '}
                            {formatMessage(messages.genericMustMin)} {currency} {minAmount}{' '}
                            {maxAmount
                                ? `${formatMessage(
                                      messages.genericAndMax,
                                  )} ${currency} ${maxAmount}`
                                : ''}
                        </p>
                    </div>
                ) : (
                    ''
                )}

                {(errorMinAmount && !focus) || (errorMaxAmount && !focus) ? (
                    <p className="error">
                        <WarningIcon size={16} />
                        {formatMessage(messages.errorInvalidAmount)}
                    </p>
                ) : (
                    ''
                )}

                <div className="quantity-button">
                    <QuantityButton
                        data-test="quantity-button"
                        increaseQuantity={() => handleCountIncrease()}
                        decreaseQuantity={() => handleCountDecrease()}
                        changeQuantity={(value) => handleSetQuantity(value)}
                        quantity={quantity}
                    />

                    <Button data-test="button-add-to-bag" onClick={() => addToBag()}>
                        {action === 'add'
                            ? formatMessage(messages.genericLabelAddToBag)
                            : formatMessage(messages.genericLabelUpdate)}
                    </Button>
                </div>
                {recipient.length ? (
                    <div className="list-recipients">
                        {recipient.map((el, index) => (
                            <Recipient
                                key={index}
                                index={index + 1}
                                quantity={quantity}
                                callback={(data) => addRecipient(data)}
                                value={el}
                                added={added}
                            />
                        ))}
                    </div>
                ) : (
                    ''
                )}
            </div>
            <hr className="horizontal-bar" />
        </div>
    );
}

export default injectIntl(ShoppingAmount);
